import client from './client';

// Auth Services
export const authService = {
  register: (data) => client.post('/api/auth/register', data),
  login: (data) => client.post('/api/auth/login', data),
  getMe: () => client.get('/api/auth/me'),
  updateTheme: (theme) => client.put('/api/auth/theme', { theme }),
  updateProfile: (data) => client.put('/api/auth/profile', data),
  updatePassword: (data) => client.put('/api/auth/password', data),
};

// Post Services
export const postService = {
  list: (params) => {
    const query = new URLSearchParams(params).toString();
    return client.get(`/api/posts?${query}`);
  },
  get: (id) => client.get(`/api/posts/${id}`),
  create: (data) => client.post('/api/posts', data),
  update: (id, data) => client.put(`/api/posts/${id}`, data),
  updateStatus: (id, status) => client.put(`/api/posts/${id}/status`, { status }),
  delete: (id) => client.delete(`/api/posts/${id}`),
  togglePin: (id) => client.put(`/api/posts/${id}/pin`),
  updateRank: (id, rank) => client.put(`/api/posts/${id}/rank`, { rank }),
  vote: (id) => client.post(`/api/posts/${id}/vote`),
  unvote: (id) => client.delete(`/api/posts/${id}/vote`),
  related: (id) => client.get(`/api/posts/${id}/related`),
};

// Achievement Services - 依個人貢獻統計計算成就徽章（唯讀，僅限本人）
export const achievementService = {
  getMe: () => client.get('/api/achievements/me'),
};

// Custom Badge Services - 自訂榮譽徽章（公開讀取，供個人資料頁顯示某使用者已獲得的榮譽徽章）
export const badgeService = {
  getUserBadges: (userId) => client.get(`/api/badges/user/${userId}`),
};

// Poll Services - 投票（民調）。附貼文的投票隨貼文詳情帶出；獨立投票（跑馬燈用）以 create 建立。
export const pollService = {
  get: (id) => client.get(`/api/polls/${id}`),
  create: (data) => client.post('/api/polls', data),
  vote: (id, optionIds) => client.post(`/api/polls/${id}/vote`, { option_ids: optionIds }),
  close: (id) => client.put(`/api/polls/${id}/close`, {}),
};

// Bounty Services - 懸賞版（報修/任務），含領取、轉派、協作、完成、結案、退回
export const bountyService = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return client.get(`/api/bounties${query ? `?${query}` : ''}`);
  },
  // 我領取的（負責或協作中）
  mine: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return client.get(`/api/bounties/mine${query ? `?${query}` : ''}`);
  },
  get: (id) => client.get(`/api/bounties/${id}`),
  create: (data) => client.post('/api/bounties', data),
  update: (id, data) => client.put(`/api/bounties/${id}`, data),
  delete: (id) => client.delete(`/api/bounties/${id}`),
  claim: (id) => client.post(`/api/bounties/${id}/claim`),
  transfer: (id, userId) => client.post(`/api/bounties/${id}/transfer`, { user_id: userId }),
  addCollaborators: (id, userIds) => client.post(`/api/bounties/${id}/collaborators`, { user_ids: userIds }),
  removeCollaborator: (id, userId) => client.delete(`/api/bounties/${id}/collaborators/${userId}`),
  // 點數分配：取得目前分配草稿、整組設定分配（allocations: [{ user_id, points }]，總和需等於懸賞點數）
  getAllocation: (id) => client.get(`/api/bounties/${id}/allocation`),
  setAllocation: (id, allocations) => client.put(`/api/bounties/${id}/allocation`, { allocations }),
  complete: (id) => client.post(`/api/bounties/${id}/complete`),
  // 收回結案申請（負責人）或退回結案（開立者/管理者），讓 SUBMITTED 回到 CLAIMED
  uncomplete: (id) => client.post(`/api/bounties/${id}/uncomplete`),
  close: (id) => client.post(`/api/bounties/${id}/close`),
  giveBack: (id) => client.post(`/api/bounties/${id}/return`),
  // 輕量計數端點：全站有效懸賞數與使用者領取中數量（供導覽列/側欄徽章輪詢）
  counts: () => client.get('/api/bounties/counts'),
};

// Permission Services - 角色 × 功能權限（矩陣設定）、動態權限目錄管理、個人加掛權限指派與目前使用者有效權限查詢
export const permissionService = {
  me: () => client.get('/api/permissions/me'),
  getMatrix: () => client.get('/api/permissions/matrix'),
  setCell: (roleCode, permissionKey, granted) => client.put('/api/permissions/matrix', { role_code: roleCode, permission_key: permissionKey, granted }),
  // 權限目錄（動態，可自由新增自訂權限）
  catalog: () => client.get('/api/permissions/catalog'),
  createPermission: (data) => client.post('/api/permissions/catalog', data),
  updatePermission: (key, data) => client.put(`/api/permissions/catalog/${encodeURIComponent(key)}`, data),
  deletePermission: (key) => client.delete(`/api/permissions/catalog/${encodeURIComponent(key)}`),
  // 個人加掛權限（pop up 多選指派）
  getUserPermissions: (userId) => client.get(`/api/permissions/user/${userId}`),
  setUserPermissions: (userId, permissionKeys) => client.put(`/api/permissions/user/${userId}`, { permission_keys: permissionKeys }),
};

// Reward Services - 點數兌換商城（商品上架、兌換申請、審核、明細）
export const rewardService = {
  // 商品；all=true 取得含停用（需上架/審核權限）
  list: (all = false) => client.get(`/api/rewards${all ? '?all=1' : ''}`),
  create: (data) => client.post('/api/rewards', data),
  update: (id, data) => client.put(`/api/rewards/${id}`, data),
  remove: (id) => client.delete(`/api/rewards/${id}`),
  // 兌換流程
  redeem: (id, data) => client.post(`/api/rewards/${id}/redeem`, data),
  approve: (redemptionId) => client.post(`/api/rewards/redemptions/${redemptionId}/approve`, {}),
  reject: (redemptionId, reviewNote = '') => client.post(`/api/rewards/redemptions/${redemptionId}/reject`, { review_note: reviewNote }),
  cancel: (redemptionId) => client.post(`/api/rewards/redemptions/${redemptionId}/cancel`, {}),
  // 查詢
  myRedemptions: () => client.get('/api/rewards/redemptions/mine'),
  reviewQueue: (status) => client.get(`/api/rewards/redemptions${status ? `?status=${status}` : ''}`),
  itemRedemptions: (id, status) => client.get(`/api/rewards/${id}/redemptions${status ? `?status=${status}` : ''}`),
};

// Notification Services
export const notificationService = {
  list: () => client.get('/api/notifications'),
  unreadCount: () => client.get('/api/notifications/unread-count'),
  markRead: (id) => client.put(`/api/notifications/${id}/read`),
  markAllRead: () => client.put('/api/notifications/read-all'),
};

// Bookmark Services
export const bookmarkService = {
  list: () => client.get('/api/bookmarks'),
  add: (postId) => client.post(`/api/bookmarks/${postId}`),
  remove: (postId) => client.delete(`/api/bookmarks/${postId}`),
};

// Subscription Services - 訂閱頻道或標籤，該領域有新文章時收到通知
export const subscriptionService = {
  list: () => client.get('/api/subscriptions'),
  subscribe: (targetType, targetId) => client.post('/api/subscriptions', { target_type: targetType, target_id: targetId }),
  unsubscribe: (targetType, targetId) => client.delete(`/api/subscriptions/${targetType}/${targetId}`),
};

// User Directory Services
export const userService = {
  list: (params) => {
    const query = new URLSearchParams(params).toString();
    return client.get(`/api/users?${query}`);
  },
  // 名錄管理：具 directory.manage 權限者編輯名錄欄位（不含角色/狀態/密碼/帳號/信箱）
  updateDirectory: (id, data) => client.put(`/api/users/${id}/directory`, data),
};

// Comment Services
export const commentService = {
  list: (postId) => client.get(`/api/comments/post/${postId}`),
  create: (postId, data) => client.post(`/api/comments/post/${postId}`, data),
  update: (id, data) => client.put(`/api/comments/${id}`, data),
  delete: (id) => client.delete(`/api/comments/${id}`),
  vote: (id) => client.post(`/api/comments/${id}/vote`),
  unvote: (id) => client.delete(`/api/comments/${id}/vote`),
};

// Tag Services
export const tagService = {
  list: () => client.get('/api/tags'),
  get: (id) => client.get(`/api/tags/${id}`),
  create: (data) => client.post('/api/tags/custom', data),
  getPosts: (id) => client.get(`/api/tags/${id}/posts`),
};

// Accepted Answer Services
export const acceptedAnswerService = {
  set: (postId, commentId) => client.post(`/api/posts/${postId}/accept/${commentId}`),
  remove: (postId) => client.delete(`/api/posts/${postId}/accept`),
};

// Stats Services
export const statsService = {
  getMe: () => client.get('/api/stats/me'),
  getRankings: () => client.get('/api/stats/rankings'),
};

// Level Services
export const levelService = {
  getUserLevel: (userId) => client.get(`/api/levels/user/${userId}`),
  getConfigs: () => client.get('/api/levels/configs'),
  createConfig: (data) => client.post('/api/levels/configs', data),
  updateConfig: (level, data) => client.put(`/api/levels/configs/${level}`, data),
  deleteConfig: (level) => client.delete(`/api/levels/configs/${level}`),
  setJoinedAt: (userId, joined_at) => client.put(`/api/levels/user/${userId}/joined`, { joined_at }),
};

// Channel Services
export const channelService = {
  list: () => client.get('/api/channels'),
  listAll: () => client.get('/api/channels/all'),
  create: (data) => client.post('/api/channels', data),
  update: (id, data) => client.put(`/api/channels/${id}`, data),
  delete: (id) => client.delete(`/api/channels/${id}`),
};

// Menu Services
export const menuService = {
  list: () => client.get('/api/menu'),
  listAll: () => client.get('/api/menu/all'),
  create: (data) => client.post('/api/menu', data),
  update: (id, data) => client.put(`/api/menu/${id}`, data),
  delete: (id) => client.delete(`/api/menu/${id}`),
};

// Upload Services
export const uploadService = {
  upload: (formData) => client.post('/api/uploads', formData),
  listByPost: (postId) => client.get(`/api/uploads/post/${postId}`),
  linkToPost: (attachmentId, postId) => client.put(`/api/uploads/${attachmentId}/post`, { post_id: postId }),
  listByBounty: (bountyId) => client.get(`/api/uploads/bounty/${bountyId}`),
  linkToBounty: (attachmentId, bountyId) => client.put(`/api/uploads/${attachmentId}/bounty`, { bounty_id: bountyId }),
  delete: (id) => client.delete(`/api/uploads/${id}`),
  getFileUrl: (filePath) => `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${filePath.startsWith('/api/') ? filePath : `/api/uploads/file/${filePath}`}`,
  getDownloadUrl: (id) => `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/uploads/download/${id}`,
  browseAdmin: (dir = '', root = 'tech-forum') => client.get(`/api/uploads/admin/browse?dir=${encodeURIComponent(dir)}&root=${encodeURIComponent(root)}`),
  migrateAdminFiles: (payload) => client.post('/api/uploads/admin/migrate', payload),
  deleteAdminFile: (path) => client.delete(`/api/uploads/admin/file?path=${encodeURIComponent(path)}`),
};

// Settings Services
export const settingsService = {
  getAll: () => client.get('/api/settings'),
  getAllAdmin: () => client.get('/api/settings/all'),
  update: (key, value) => client.put(`/api/settings/${key}`, { value }),
};

// Marquee Services
export const marqueeService = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return client.get(`/api/marquees${query ? `?${query}` : ''}`);
  },
};

// Admin Services
export const adminService = {
  users: {
    list: (params) => {
      const query = new URLSearchParams(params).toString();
      return client.get(`/api/admin/users?${query}`);
    },
    get: (id) => client.get(`/api/admin/users/${id}`),
    updateStatus: (id, status) => client.put(`/api/admin/users/${id}/status`, { status }),
    resetPassword: (id) => client.put(`/api/admin/users/${id}/password`, {}),
    create: (data) => client.post('/api/admin/users', data),
    update: (id, data) => client.put(`/api/admin/users/${id}`, data),
    delete: (id) => client.delete(`/api/admin/users/${id}`),
  },
  posts: {
    delete: (id) => client.delete(`/api/admin/posts/${id}`),
  },
  comments: {
    delete: (id) => client.delete(`/api/admin/comments/${id}`),
  },
  tags: {
    list: () => client.get('/api/admin/tags'),
    create: (data) => client.post('/api/admin/tags', data),
    update: (id, data) => client.put(`/api/admin/tags/${id}`, data),
    updateStatus: (id, status) => client.put(`/api/admin/tags/${id}/status`, { status }),
    delete: (id) => client.delete(`/api/admin/tags/${id}`),
  },
  sql: {
    execute: (sql) => client.post('/api/admin/sql', { sql }),
  },
  marquees: {
    list: () => client.get('/api/admin/marquees'),
    create: (data) => client.post('/api/admin/marquees', data),
    update: (id, data) => client.put(`/api/admin/marquees/${id}`, data),
    delete: (id) => client.delete(`/api/admin/marquees/${id}`),
  },
  badges: {
    list: () => client.get('/api/admin/badges'),
    create: (data) => client.post('/api/admin/badges', data),
    update: (id, data) => client.put(`/api/admin/badges/${id}`, data),
    delete: (id) => client.delete(`/api/admin/badges/${id}`),
    recipients: (id) => client.get(`/api/admin/badges/${id}/recipients`),
    award: (id, userIds, note = '') => client.post(`/api/admin/badges/${id}/award`, { user_ids: userIds, note }),
    revoke: (id, userId) => client.delete(`/api/admin/badges/${id}/award/${userId}`),
  },
};

